import FormItem from '../ui/FormItem';
import Button from '../ui/Buttons/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  apiCustomerGetCodeWithPhone,
  apiCustomerLoginWithPhoneByCode,
} from '../../services/AuthService';
import { useEffect, useState } from 'react';
import useAuth from '../../utils/hooks/useAuth';
import FormNumericInput from '../shared/FormNumericInput';
import toast from '../ui/toast/toast';
import Notification from '../ui/Notification';

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .min(4, 'Please enter valid code.')
    .required('Please code.'),
});

const CodeForm = ({ phone, onLogin }) => {
  const { signIn } = useAuth();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isResendSubmitting, setIsResendSubmitting] = useState(false);
  const [counter, setCounter] = useState(60);
  const [errorMessage, setErrorMessage] = useState('');
  const onCounter = counter > 0;

  const handleLogin = async (values) => {
    setIsFormSubmitting(true);
    setErrorMessage('');
    let payload = {
      phone: phone,
      code: values.code,
    };

    try {
      const success = await apiCustomerLoginWithPhoneByCode(payload);
      if (success) {
        const customerSetComplete = await signIn(success.data.data.token);

        if (customerSetComplete) {
          onLogin();
        }
        setIsFormSubmitting(false);
      } else {
        setErrorMessage('Invalid verification code. Please try again.');
        setIsFormSubmitting(false);
      }
    } catch (error) {
      console.log(error);
      let message = "That code doesn't look right. Please try again!";
      if (error?.response?.data) {
        message =
          typeof error.response.data === 'string'
            ? error.response.data
            : error.response.data.message ||
              error.response.data.error ||
              message;
      }
      setErrorMessage(message);
      setIsFormSubmitting(false);
    }
  };
  const handleResend = async () => {
    setIsResendSubmitting(true);
    setErrorMessage('');
    let payload = {
      phone: phone,
    };

    try {
      const success = await apiCustomerGetCodeWithPhone(payload);
      if (success) {
        setCounter(120);
        toast.push(
          <Notification
            title={'Code resent!'}
            type="success"
            duration={2000}
          />,
          {
            placement: 'top-center',
          }
        );
        setIsResendSubmitting(false);
      } else {
        setErrorMessage('Failed to resend code. Please try again.');
        setIsResendSubmitting(false);
      }
    } catch (error) {
      let message = 'Failed to resend code. Please try again.';
      if (error?.response?.data) {
        message =
          typeof error.response.data === 'string'
            ? error.response.data
            : error.response.data.message ||
              error.response.data.error ||
              message;
      }
      setErrorMessage(message);
      setIsResendSubmitting(false);
    }
  };

  useEffect(() => {
    const TimerInt =
      counter > 0 &&
      setInterval(() => {
        setCounter((time) => time - 1);
      }, 1000);
    return () => {
      clearInterval(TimerInt);
    };
  }, [counter]);

  return (
    <div className="w-full">
      <Formik
        initialValues={{
          code: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleLogin(values);
        }}
      >
        {({ touched, errors, isValid, dirty }) => {
          return (
            <Form>
              <FormItem
                label="Code"
                invalid={errors.code && touched.code}
                errorMessage={errors.code}
              >
                <Field name="code">
                  {({ form, field }) => {
                    return (
                      <FormNumericInput
                        className="text-center"
                        placeholder="----"
                        format="####"
                        field={field}
                        form={form}
                        onValueChange={(val) => {
                          form.setFieldValue(field.name, val.value);
                          // Clear stale error message as soon as the
                          // customer starts typing a new code.
                          if (errorMessage) setErrorMessage('');
                        }}
                      />
                    );
                  }}
                </Field>
              </FormItem>
              {errorMessage && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 text-center">
                    {errorMessage}
                  </p>
                </div>
              )}
              <FormItem className="mt-8">
                <Button
                  loading={isFormSubmitting}
                  className="w-full px-4 h-12"
                  variant="secondary"
                  type="submit"
                  disabled={!isValid || !dirty}
                >
                  Login
                </Button>
                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    Didn't receive the code?
                  </p>
                  {onCounter ? (
                    <p className="text-sm text-gray-500">
                      You have to wait{' '}
                      <span className="font-semibold text-gray-900">
                        {Math.floor(counter / 60)}:
                        {(counter % 60).toString().padStart(2, '0')} mins
                      </span>{' '}
                      to get code again
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={isResendSubmitting}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResendSubmitting ? 'Sending...' : 'Resend Code'}
                    </button>
                  )}
                </div>
              </FormItem>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CodeForm;
